import React from 'react';
import styled from 'styled-components';

import { GrowthPlannerCheck, ResponseTone } from '../../../types';
import { responseTone, responseType, rsColor, rsHandoffColor, rsv } from '../../responseScale';
import { StepHeading } from './handoffParts';

/** 6473:12841 / 12853: the hand-off draws its verdicts on its own hues. */
const CHECK_INK: Partial<Record<ResponseTone, string>> = {
  success: rsHandoffColor.passed,
  warning: rsHandoffColor.caution,
  error: rsHandoffColor.unavailable
};

const checkInk = (tone: ResponseTone | undefined): string => {
  if (tone === undefined) {
    return rsColor.text;
  }
  return CHECK_INK[tone] ?? responseTone(tone).ink;
};

const Section = styled.section`
  margin-top: ${rsv.space('checksTop')}px;
`;

// 6473:12838: r12 under a 1px #E4E8F0 rule; the rows fill the left column first.
const Table = styled.dl<{ $rows: number }>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(${({ $rows }) => $rows}, auto);
  column-gap: ${rsv.space('checksColumnGap')}px;
  margin: ${rsv.space('checksTableTop')}px 0 0;
  border: 1px solid ${rsHandoffColor.cardRule};
  border-radius: ${rsv.radius('row')}px;
`;

// 6473:12839: 35 tall with its rule, the label at +12 / +10; a column's last row has no rule.
const Row = styled.div<{ $ruled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rsv.space('checkRow')};
  border-bottom: ${({ $ruled }) => ($ruled ? `1px solid ${rsHandoffColor.cardRule}` : 'none')};
  ${responseType('handoffBody')}

  > dt {
    min-width: 0;
    color: ${rsColor.text};
  }

  > dd {
    flex: 0 0 auto;
    margin: 0 0 0 ${rsv.space('rowGap')}px;
    font-weight: 700;
  }
`;

export interface IHandoffChecksProps {
  heading: string;
  rows: GrowthPlannerCheck[];
}

/** Figma 6473:12837-12853: "2 · Handoff checks", each verdict as the payload sends it. */
const HandoffChecks: React.FC<IHandoffChecksProps> = ({ heading, rows }) => {
  const perColumn = Math.ceil(rows.length / 2);
  return (
    <Section data-testid="orbit-handoff-checks">
      <StepHeading>{heading}</StepHeading>
      <Table $rows={perColumn}>
        {rows.map((row, index) => (
          <Row
            key={row.id}
            $ruled={index !== perColumn - 1 && index !== rows.length - 1}
            data-testid="orbit-handoff-check"
            data-check-id={row.id}
          >
            <dt>{row.label}</dt>
            <dd style={{ color: checkInk(row.tone) }}>{row.value}</dd>
          </Row>
        ))}
      </Table>
    </Section>
  );
};

export default HandoffChecks;
